import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { voucherAPI } from '../services/api';
import { useAuth } from './AuthContext';

const VoucherContext = createContext();

export const useVoucher = () => {
  const context = useContext(VoucherContext);
  if (!context) {
    throw new Error('useVoucher must be used within a VoucherProvider');
  }
  return context;
};

export const VoucherProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Listen for storage changes to sync vouchers across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'vouchers') {
        try {
          const savedVouchers = localStorage.getItem('vouchers');
          if (savedVouchers) {
            const parsedVouchers = JSON.parse(savedVouchers);
            setVouchers(parsedVouchers);
          } else {
            setVouchers([]);
          }
        } catch (error) {
          logger.error('Error syncing vouchers from storage:', error);
        }
      }
    };

    const handleVouchersUpdated = (e) => {
      try {
        // Prefer vouchers from event detail if provided, otherwise read from localStorage
        const updatedVouchers = e.detail?.vouchers || JSON.parse(localStorage.getItem('vouchers') || '[]');
        const validVouchers = updatedVouchers.filter(v => {
          const isValid = v && v.id && v.code;
          if (!isValid) {
            return false;
          }
          
          // Filter out used promotional vouchers (one-time use or fully used)
          const isPromotional = !v.isDailyLoginReward && !v.description?.includes('7-Day Daily Login Reward');
          if (isPromotional) {
            const isOneTimeUse = v.maxUses === 1;
            const hasBeenUsed = v.usedCount !== undefined && v.usedCount >= 1;
            const isFullyUsed = v.maxUses !== undefined && v.usedCount !== undefined && v.usedCount >= v.maxUses;
            
            // Remove one-time use promotional vouchers that have been used
            if (isOneTimeUse && hasBeenUsed) {
              return false;
            }
            // Remove fully used promotional vouchers
            if (isFullyUsed) {
              return false;
            }
          }
          
          return true;
        });
        setVouchers(validVouchers);
      } catch (error) {
        logger.error('Error syncing vouchers from custom event:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('vouchers-updated', handleVouchersUpdated);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('vouchers-updated', handleVouchersUpdated);
    };
  }, []);

  // Initialize vouchers from database if authenticated, otherwise from localStorage
  useEffect(() => {
    const initializeVouchers = async () => {
      if (isAuthenticated) {
        // Fetch from database if authenticated
        try {
          setLoading(true);
          const response = await voucherAPI.getVouchers();
          if (response.success && response.data) {
            const fetchedVouchers = response.data.vouchers || [];
            const formattedVouchers = fetchedVouchers.map(v => ({
              id: v._id || v.id,
              code: v.code,
              description: v.description,
              discountType: v.discountType,
              discountValue: v.discountValue,
              maximumDiscount: v.maximumDiscount,
              minimumAmount: v.minimumAmount,
              maxUses: v.maxUses,
              usedCount: v.usedCount || 0,
              expiryDate: v.expiryDate,
              applicableToFirstTimeOnly: v.applicableToFirstTimeOnly,
              isActive: v.isActive,
              createdAt: v.createdAt,
              isDailyLoginReward: v.isDailyLoginReward,
              isReferralReward: v.isReferralReward,
              voucherType: v.voucherType,
              isPublic: v.isPublic !== undefined ? v.isPublic : false,
              userId: v.user?._id || v.user
            }));
            setVouchers(formattedVouchers);
            // Cache in localStorage
            try {
              localStorage.setItem('vouchers', JSON.stringify(formattedVouchers));
            } catch (e) {
              logger.warn('Failed to cache vouchers:', e);
            }
          }
        } catch (error) {
          logger.error('Error fetching vouchers from database:', error);
          // Fallback to localStorage
          try {
            const savedVouchers = localStorage.getItem('vouchers');
            if (savedVouchers) {
              const parsedVouchers = JSON.parse(savedVouchers);
              setVouchers(parsedVouchers.filter(v => v && v.id && v.code));
            }
          } catch (e) {
            logger.error('Error loading from localStorage fallback:', e);
          }
        } finally {
          setLoading(false);
        }
      } else {
        // Load from localStorage if not authenticated
        try {
          const savedVouchers = localStorage.getItem('vouchers');
          if (savedVouchers) {
            const parsedVouchers = JSON.parse(savedVouchers);
            const validVouchers = parsedVouchers.filter(v => v && v.id && v.code);
            setVouchers(validVouchers);
          } else {
            setVouchers([]);
          }
        } catch (error) {
          logger.error('Error loading vouchers from localStorage:', error);
          setVouchers([]);
        } finally {
          setLoading(false);
        }
      }
    };

    initializeVouchers();
  }, [isAuthenticated]);

  // Save vouchers to localStorage whenever vouchers change
  // Also filter out used promotional vouchers before saving
  useEffect(() => {
    if (!loading && vouchers.length >= 0) {
      // Filter out used promotional vouchers before saving to localStorage
      const vouchersToSave = vouchers.filter(voucher => {
        if (!voucher) return false;
        
        const isPromotional = !voucher.isDailyLoginReward && !voucher.description?.includes('7-Day Daily Login Reward');
        if (isPromotional) {
          const isOneTimeUse = voucher.maxUses === 1;
          const hasBeenUsed = voucher.usedCount !== undefined && voucher.usedCount >= 1;
          const isFullyUsed = voucher.maxUses !== undefined && voucher.usedCount !== undefined && voucher.usedCount >= voucher.maxUses;
          
          // Remove one-time use promotional vouchers that have been used
          if (isOneTimeUse && hasBeenUsed) {
            return false;
          }
          // Remove fully used promotional vouchers
          if (isFullyUsed) {
            return false;
          }
        }
        return true;
      });
      
      localStorage.setItem('vouchers', JSON.stringify(vouchersToSave));
    }
  }, [vouchers, loading]);

  const addVoucher = async (newVoucherData) => {
    // If authenticated, check if this is a system-generated voucher
    // System-generated vouchers (referral rewards, daily login rewards) are already created by the backend
    // We should just refresh from database instead of trying to create them
    const isSystemGenerated = newVoucherData.isReferralReward || 
                              newVoucherData.isDailyLoginReward || 
                              newVoucherData.voucherType === 'referral';
    
    if (isAuthenticated && !isSystemGenerated) {
      // Only try to create voucher in database if it's not system-generated
      // System-generated vouchers are already created by backend endpoints
      try {
        const response = await voucherAPI.createVoucher(newVoucherData);
        if (response.success && response.data) {
          const createdVoucher = response.data.voucher;
          const formattedVoucher = {
            id: createdVoucher._id || createdVoucher.id,
            code: createdVoucher.code,
            description: createdVoucher.description,
            discountType: createdVoucher.discountType,
            discountValue: createdVoucher.discountValue,
            maximumDiscount: createdVoucher.maximumDiscount,
            minimumAmount: createdVoucher.minimumAmount,
            maxUses: createdVoucher.maxUses,
            usedCount: createdVoucher.usedCount || 0,
            expiryDate: createdVoucher.expiryDate,
            applicableToFirstTimeOnly: createdVoucher.applicableToFirstTimeOnly,
            isActive: createdVoucher.isActive,
            createdAt: createdVoucher.createdAt,
            isDailyLoginReward: createdVoucher.isDailyLoginReward,
            isReferralReward: createdVoucher.isReferralReward,
            voucherType: createdVoucher.voucherType,
            isPublic: createdVoucher.isPublic !== undefined ? createdVoucher.isPublic : false,
            userId: createdVoucher.user?._id || createdVoucher.user
          };
          
          // Refresh vouchers from database
          const voucherResponse = await voucherAPI.getVouchers();
          if (voucherResponse.success && voucherResponse.data) {
            const fetchedVouchers = voucherResponse.data.vouchers || [];
            const formattedVouchers = fetchedVouchers.map(v => ({
              id: v._id || v.id,
              code: v.code,
              description: v.description,
              discountType: v.discountType,
              discountValue: v.discountValue,
              maximumDiscount: v.maximumDiscount,
              minimumAmount: v.minimumAmount,
              maxUses: v.maxUses,
              usedCount: v.usedCount || 0,
              expiryDate: v.expiryDate,
              applicableToFirstTimeOnly: v.applicableToFirstTimeOnly,
              isActive: v.isActive,
              createdAt: v.createdAt,
              isDailyLoginReward: v.isDailyLoginReward,
              isReferralReward: v.isReferralReward,
              voucherType: v.voucherType,
              isPublic: v.isPublic !== undefined ? v.isPublic : false,
              userId: v.user?._id || v.user
            }));
            setVouchers(formattedVouchers);
            // Cache in localStorage
            try {
              localStorage.setItem('vouchers', JSON.stringify(formattedVouchers));
            } catch (e) {
              logger.warn('Failed to cache vouchers:', e);
            }
          }
          return formattedVoucher;
        }
      } catch (error) {
        // If it's a 403 error (admin required), it means this is a system-generated voucher
        // Just fall through to localStorage handling - no need to log or throw
        if (error.response?.status === 403) {
          // System-generated voucher - skip database creation
        } else {
          // Only throw non-403 errors (don't log - expected behavior for system vouchers)
          throw error;
        }
      }
    }
    
    // For system-generated vouchers or if database creation failed/skipped:
    // Just refresh from database to get the voucher that was already created by backend
    if (isAuthenticated && isSystemGenerated) {
      try {
        // Refresh vouchers from database to get the system-generated voucher
        const voucherResponse = await voucherAPI.getVouchers();
        if (voucherResponse.success && voucherResponse.data) {
          const fetchedVouchers = voucherResponse.data.vouchers || [];
          const formattedVouchers = fetchedVouchers.map(v => ({
            id: v._id || v.id,
            code: v.code,
            description: v.description,
            discountType: v.discountType,
            discountValue: v.discountValue,
            maximumDiscount: v.maximumDiscount,
            minimumAmount: v.minimumAmount,
            maxUses: v.maxUses,
            usedCount: v.usedCount || 0,
            expiryDate: v.expiryDate,
            applicableToFirstTimeOnly: v.applicableToFirstTimeOnly,
            isActive: v.isActive,
            createdAt: v.createdAt,
            isDailyLoginReward: v.isDailyLoginReward,
            isReferralReward: v.isReferralReward,
            voucherType: v.voucherType,
            isPublic: v.isPublic !== undefined ? v.isPublic : false,
            userId: v.user?._id || v.user
          }));
          setVouchers(formattedVouchers);
          // Cache in localStorage
          try {
            localStorage.setItem('vouchers', JSON.stringify(formattedVouchers));
          } catch (e) {
            logger.warn('Failed to cache vouchers:', e);
          }
          // Return the voucher if found
          const foundVoucher = formattedVouchers.find(v => v.code === newVoucherData.code);
          if (foundVoucher) {
            return foundVoucher;
          }
        }
      } catch (error) {
        logger.error('Error refreshing vouchers from database:', error);
        // Fall through to localStorage handling
      }
    }
    
    // Fallback: localStorage only (for non-authenticated users)
    const voucherId = newVoucherData.id || Date.now() + Math.random();
    
    const newVoucher = {
      ...newVoucherData,
      id: voucherId,
      usedCount: newVoucherData.usedCount || 0,
      createdAt: newVoucherData.createdAt || new Date().toISOString(),
      isActive: newVoucherData.isActive !== undefined ? newVoucherData.isActive : true
    };
    
    setVouchers(prevVouchers => {
      // Check if voucher with this code already exists
      const existingVoucher = prevVouchers.find(v => v.code === newVoucherData.code);
      if (existingVoucher) {
        logger.warn(`Voucher with code ${newVoucherData.code} already exists`);
        return prevVouchers;
      }
      
      const updated = [...prevVouchers, newVoucher];
      // Immediately save to localStorage
      try {
        localStorage.setItem('vouchers', JSON.stringify(updated));
      } catch (error) {
        logger.error('Failed to save voucher to localStorage:', error);
      }
      
      // Dispatch event to sync across tabs
      window.dispatchEvent(new CustomEvent('vouchers-updated', { 
        detail: { vouchers: updated }
      }));
      
      return updated;
    });
    
    return newVoucher;
  };

  const updateVoucher = async (voucherId, updatedData) => {
    // If authenticated, update in database
    if (isAuthenticated) {
      try {
        const response = await voucherAPI.updateVoucher(voucherId, updatedData);
        if (response.success && response.data && response.data.voucher) {
          // Update the voucher in state immediately with the response data
          const updatedVoucher = response.data.voucher;
          setVouchers(prevVouchers => 
            prevVouchers.map(voucher => {
              if (voucher.id === voucherId || voucher.id === updatedVoucher._id) {
                return {
                  ...voucher,
                  ...updatedData,
                  id: updatedVoucher._id || voucherId,
                  isActive: updatedVoucher.isActive !== undefined ? updatedVoucher.isActive : voucher.isActive,
                  isPublic: updatedVoucher.isPublic !== undefined ? updatedVoucher.isPublic : (updatedData.isPublic !== undefined ? updatedData.isPublic : voucher.isPublic)
                };
              }
              return voucher;
            })
          );
          return;
        }
      } catch (error) {
        logger.error('Error updating voucher in database:', error);
        // Fall through to localStorage fallback
      }
    }

    // Fallback: localStorage only
    setVouchers(prevVouchers => 
      prevVouchers.map(voucher => 
        voucher.id === voucherId ? { ...voucher, ...updatedData } : voucher
      )
    );
  };

  const deleteVoucher = async (voucherId) => {
    // If authenticated, delete from database
    if (isAuthenticated) {
      try {
        const response = await voucherAPI.deleteVoucher(voucherId);
        if (response.success) {
          // Refresh vouchers from database
          const voucherResponse = await voucherAPI.getVouchers();
          if (voucherResponse.success && voucherResponse.data) {
            const fetchedVouchers = voucherResponse.data.vouchers || [];
            const formattedVouchers = fetchedVouchers.map(v => ({
              id: v._id || v.id,
              code: v.code,
              description: v.description,
              discountType: v.discountType,
              discountValue: v.discountValue,
              maximumDiscount: v.maximumDiscount,
              minimumAmount: v.minimumAmount,
              maxUses: v.maxUses,
              usedCount: v.usedCount || 0,
              expiryDate: v.expiryDate,
              applicableToFirstTimeOnly: v.applicableToFirstTimeOnly,
              isActive: v.isActive,
              createdAt: v.createdAt,
              isDailyLoginReward: v.isDailyLoginReward,
              isReferralReward: v.isReferralReward,
              voucherType: v.voucherType,
              isPublic: v.isPublic !== undefined ? v.isPublic : false,
              userId: v.user?._id || v.user
            }));
            setVouchers(formattedVouchers);
            // Cache in localStorage
            try {
              localStorage.setItem('vouchers', JSON.stringify(formattedVouchers));
            } catch (e) {
              logger.warn('Failed to cache vouchers:', e);
            }
            return;
          }
        }
      } catch (error) {
        logger.error('Error deleting voucher from database:', error);
        // Fall through to localStorage fallback
      }
    }

    // Fallback: localStorage only
    setVouchers(prevVouchers => {
      const updated = prevVouchers.filter(voucher => voucher.id !== voucherId);
      try {
        localStorage.setItem('vouchers', JSON.stringify(updated));
      } catch (e) {
        logger.error('Failed to update localStorage:', e);
      }
      return updated;
    });
  };

  const clearAllVouchers = () => {
    setVouchers([]);
    localStorage.removeItem('vouchers');
    // Dispatch event to sync across tabs
    window.dispatchEvent(new CustomEvent('vouchers-updated', { 
      detail: { vouchers: [] }
    }));
  };

  const clearPromotionalAndLoginVouchers = () => {
    setVouchers(prevVouchers => {
      // Keep only vouchers that are NOT promotional and NOT 7-day login rewards
      const filteredVouchers = prevVouchers.filter(voucher => {
        if (!voucher) return false;
        
        // Remove promotional vouchers
        const isPromotional = !voucher.isDailyLoginReward && !voucher.description?.includes('7-Day Daily Login Reward') && !voucher.description?.includes('7 day login reward');
        if (isPromotional) {
          return false;
        }
        
        // Remove 7-day login reward vouchers
        const is7DayReward = voucher.isDailyLoginReward || voucher.description?.includes('7-Day Daily Login Reward') || voucher.description?.includes('7 day login reward');
        if (is7DayReward) {
          return false;
        }
        
        return true;
      });
      
      // Update localStorage
      localStorage.setItem('vouchers', JSON.stringify(filteredVouchers));
      
      // Dispatch event to sync across tabs
      window.dispatchEvent(new CustomEvent('vouchers-updated', { 
        detail: { vouchers: filteredVouchers }
      }));
      
      return filteredVouchers;
    });
  };

  const toggleVoucherActive = async (voucherId) => {
    // Find the voucher to get current state
    const voucher = vouchers.find(v => v.id === voucherId);
    if (!voucher) {
      logger.warn(`Voucher ${voucherId} not found`);
      return;
    }

    // If authenticated, update in database
    if (isAuthenticated) {
      try {
        const response = await voucherAPI.updateVoucher(voucherId, {
          isActive: !voucher.isActive
        });
        if (response.success && response.data && response.data.voucher) {
          // Update the voucher in state immediately with the response data
          const updatedVoucher = response.data.voucher;
          setVouchers(prevVouchers => 
            prevVouchers.map(v => {
              if (v.id === voucherId || v.id === updatedVoucher._id) {
                return {
                  ...v,
                  isActive: updatedVoucher.isActive !== undefined ? updatedVoucher.isActive : !v.isActive,
                  id: updatedVoucher._id || voucherId
                };
              }
              return v;
            })
          );
          return;
        }
      } catch (error) {
        logger.error('Error toggling voucher active status in database:', error);
        // Fall through to localStorage fallback
      }
    }

    // Fallback: localStorage only
    setVouchers(prevVouchers => 
      prevVouchers.map(v => 
        v.id === voucherId ? { ...v, isActive: !v.isActive } : v
      )
    );
  };

  const validateVoucher = (code, orderAmount, isFirstTimeCustomer = false, currentUserId = null) => {
    const normalizedCode = code.toUpperCase();
    let voucher = vouchers.find(v => 
      v.code.toUpperCase() === normalizedCode && 
      v.isActive
    );

    // If not found in current state, try to refresh from localStorage (e.g. 7-day reward just added)
    if (!voucher) {
      try {
        const savedVouchers = localStorage.getItem('vouchers');
        if (savedVouchers) {
          const parsedVouchers = JSON.parse(savedVouchers).filter(v => v && v.id && v.code);
          const found = parsedVouchers.find(v => 
            v.code.toUpperCase() === normalizedCode && 
            v.isActive
          );
          if (found) {
            voucher = found;
            // Sync context state with latest vouchers
            setVouchers(parsedVouchers);
          }
        }
      } catch (error) {
        logger.error('Error refreshing vouchers from storage during validation:', error);
      }
    }

    if (!voucher) {
      return { valid: false, error: 'Invalid voucher code' };
    }

    // IMPORTANT: Check ownership for 7-day login reward vouchers
    if (voucher.isDailyLoginReward || voucher.description?.includes('7-Day Daily Login Reward')) {
      const voucherUserId = voucher.userId;
      
      // If user is not logged in, cannot use 7-day rewards
      if (!currentUserId) {
        return { valid: false, error: 'You must be logged in to use this voucher' };
      }
      
      // If voucher has no userId (old/invalid voucher), reject it
      if (!voucherUserId) {
        return { valid: false, error: 'This voucher is not valid' };
      }
      
      // Compare user IDs (handle both string and object ID formats)
      const voucherUserIdStr = String(voucherUserId);
      const currentUserIdStr = String(currentUserId);
      
      // Only allow if userIds match exactly
      if (voucherUserIdStr !== currentUserIdStr) {
        return { valid: false, error: 'This voucher does not belong to you' };
      }
    }

    // Check expiry date
    if (new Date(voucher.expiryDate) < new Date()) {
      return { valid: false, error: 'Voucher has expired' };
    }

    // Check usage limit
    if (voucher.usedCount >= voucher.maxUses) {
      return { valid: false, error: 'Voucher usage limit reached' };
    }

    // Check minimum amount
    if (orderAmount < voucher.minimumAmount) {
      return { 
        valid: false, 
        error: `Minimum order amount is ₱${voucher.minimumAmount}` 
      };
    }

    // Check first time customer restriction
    if (voucher.applicableToFirstTimeOnly && !isFirstTimeCustomer) {
      return { valid: false, error: 'This voucher is only for first-time customers' };
    }

    return { valid: true, voucher };
  };

  const calculateDiscount = (voucher, orderAmount) => {
    let discount = 0;
    if (voucher.discountType === 'percentage') {
      discount = (orderAmount * voucher.discountValue) / 100;
      // Apply maximum discount limit if specified
      if (voucher.maximumDiscount !== undefined) {
        discount = Math.min(discount, voucher.maximumDiscount);
      }
    } else {
      discount = voucher.discountValue;
    }
    // Ensure discount doesn't exceed order amount
    return Math.min(discount, orderAmount);
  };

  const applyVoucher = (voucherId) => {
    setVouchers(prevVouchers => {
      const updatedVouchers = prevVouchers.map(voucher => {
        if (voucher.id === voucherId) {
          const newUsedCount = (voucher.usedCount || 0) + 1;
          const updatedVoucher = { ...voucher, usedCount: newUsedCount };
          
          const isOneTimeUse = voucher.maxUses === 1;
          const isFullyUsed = voucher.maxUses !== undefined && newUsedCount >= voucher.maxUses;
          
          // Remove vouchers that are one-time use or fully used
          // This includes: promotional vouchers, referral rewards, and daily login rewards
          if (isOneTimeUse || isFullyUsed) {
            // Don't include this voucher in the updated list (remove it)
            return null;
          }
          
          return updatedVoucher;
        }
        return voucher;
      });
      
      // Filter out null values (removed vouchers)
      return updatedVouchers.filter(v => v !== null);
    });
  };

  // Get active vouchers only (not expired, active, and within usage limit)
  const getActiveVouchers = () => {
    const now = new Date();
    const active = vouchers.filter(voucher => {
      // Must have required fields
      if (!voucher || !voucher.code) {
        return false;
      }
      
      // Must be active
      if (!voucher.isActive) {
        return false;
      }
      
      // Must not be expired (if expiryDate exists)
      if (voucher.expiryDate) {
        const expiryDate = new Date(voucher.expiryDate);
        if (isNaN(expiryDate.getTime()) || expiryDate < now) {
          return false;
        }
      }
      
      // Must not have reached usage limit
      if (voucher.usedCount !== undefined && voucher.maxUses !== undefined) {
        if (voucher.usedCount >= voucher.maxUses) {
          return false;
        }
      }
      
      return true;
    });
    return active;
  };

  // Track last refresh time to prevent rate limiting
  const lastRefreshTime = useRef(0);
  const REFRESH_COOLDOWN = 5000; // 5 seconds cooldown between refreshes

  // Force refresh vouchers from database (for admin pages)
  const refreshVouchersFromDatabase = useCallback(async (isAdmin = false, silent = false) => {
    if (!isAuthenticated) {
      logger.warn('Cannot refresh vouchers: user not authenticated');
      return;
    }

    // Prevent rapid successive calls
    const now = Date.now();
    if (now - lastRefreshTime.current < REFRESH_COOLDOWN) {
      logger.debug(`Skipping voucher refresh - too soon (${now - lastRefreshTime.current}ms ago)`);
      return;
    }
    lastRefreshTime.current = now;

    try {
      // Only show loading state if not silent
      if (!silent) {
        setLoading(true);
      }
      // Use admin endpoint if admin, otherwise use regular endpoint
      const response = isAdmin 
        ? await voucherAPI.getAllVouchersAdmin()
        : await voucherAPI.getVouchers();
      
      if (response.success && response.data) {
        const fetchedVouchers = response.data.vouchers || [];
        const formattedVouchers = fetchedVouchers.map(v => ({
          id: v._id || v.id,
          code: v.code,
          description: v.description,
          discountType: v.discountType,
          discountValue: v.discountValue,
          maximumDiscount: v.maximumDiscount,
          minimumAmount: v.minimumAmount,
          maxUses: v.maxUses,
          usedCount: v.usedCount || 0,
          expiryDate: v.expiryDate,
          applicableToFirstTimeOnly: v.applicableToFirstTimeOnly,
          isActive: v.isActive,
          createdAt: v.createdAt,
          isDailyLoginReward: v.isDailyLoginReward,
          isReferralReward: v.isReferralReward,
          voucherType: v.voucherType,
          isPublic: v.isPublic !== undefined ? v.isPublic : false,
          userId: v.user?._id || v.user
        }));
        
        setVouchers(formattedVouchers);
        // Cache in localStorage
        try {
          localStorage.setItem('vouchers', JSON.stringify(formattedVouchers));
        } catch (e) {
          logger.warn('Failed to cache vouchers:', e);
        }
        if (!silent) {
        } else {
        }
      }
    } catch (error) {
      // Don't log rate limit errors as errors
      if (error?.response?.status === 429) {
        logger.debug('Rate limit hit when refreshing vouchers - will retry later');
      } else if (!silent) {
        logger.error('Error refreshing vouchers from database:', error);
      } else {
        logger.debug('Background voucher refresh error:', error);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [isAuthenticated]);

  const value = {
    vouchers,
    loading,
    addVoucher,
    updateVoucher,
    deleteVoucher,
    toggleVoucherActive,
    validateVoucher,
    calculateDiscount,
    applyVoucher,
    getActiveVouchers,
    clearAllVouchers,
    clearPromotionalAndLoginVouchers,
    refreshVouchersFromDatabase
  };

  return (
    <VoucherContext.Provider value={value}>
      {children}
    </VoucherContext.Provider>
  );
}; 